import { Badge, Button, Dropdown, Empty, Menu, Spin } from "antd"
import { FilterOutlined } from "@ant-design/icons"
import { useIsAuthenticated, useMsal } from "@azure/msal-react"
import { Fragment, useCallback, useEffect, useRef, useState } from "react"
import { Report } from "notiflix"
import { useNavigate } from "react-router"
import moment from "moment"

import Navbar from "../../atoms/navbar"
import Footer from "../../atoms/footer"
import "./styles.css"

import { loginRequest } from "../../config/api.config"
import HeaderFarms from "../../utils/header_farms"
import API from "../../services"

const DEFAULT_STATUS = "Waiting Approval"
const DEFAULT_PER_PAGE = 20
const DEFAULT_META = {
  page: 1,
  per_page: DEFAULT_PER_PAGE,
  total_items: 0,
  total_pages: 0,
  has_more: false,
}

const STATUS_OPTIONS = ["Waiting Approval", "Reject", "Approved", "Cancel"]

const mergePendingItems = (currentItems, nextItems) => {
  const mergedMap = new Map()

  ;[...currentItems, ...nextItems].forEach(item => {
    const key = item?.id || item?._id

    if (key) {
      mergedMap.set(key, item)
    }
  })

  return Array.from(mergedMap.values())
}

const getRibbonColor = status => {
  switch (status) {
    case "Waiting Approval":
      return "#FF9900"
    case "Approved":
      return "primary"
    case "Reject":
    case "Cancel":
      return "#F5222D"
    default:
      return "#1677ff"
  }
}

function WFPendingList() {
  const navigate = useNavigate()
  const { instance, accounts } = useMsal()
  const isAuthenticated = useIsAuthenticated()

  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [pendingList, setPendingList] = useState([])
  const [meta, setMeta] = useState(DEFAULT_META)
  const [filterStatus, setFilterStatus] = useState(DEFAULT_STATUS)
  const [filterFormCode, setFilterFormCode] = useState("All")
  const [forms, setForms] = useState([])
  const [accessToken, setAccessToken] = useState(null)

  const observerTarget = useRef(null)
  const loadingRef = useRef(false)

  const activeForm = forms.find(form => form.code === filterFormCode)

  const handleLogin = useCallback(() => {
    instance.loginRedirect(loginRequest).catch(error => {
      console.error(error)
    })
  }, [instance])

  const getAccessToken = useCallback(async () => {
    if (accessToken) {
      return accessToken
    }

    const account = accounts[0]

    if (!account) {
      return null
    }

    try {
      const responseToken = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      })

      setAccessToken(responseToken.accessToken)

      return responseToken.accessToken
    } catch (error) {
      handleLogin()
      navigate("/")
      return null
    }
  }, [accessToken, accounts, handleLogin, instance, navigate])

  const fetchPendingPage = useCallback(
    async ({ page = 1, reset = false } = {}) => {
      const token = await getAccessToken()

      if (!token) {
        setLoading(false)
        setLoadingMore(false)
        return
      }

      if (reset) {
        setLoading(true)
      } else {
        setLoadingMore(true)
      }

      try {
        const response = await API.workflowPending(HeaderFarms(token), {
          status: filterStatus,
          form_code: filterFormCode === "All" ? undefined : filterFormCode,
          page,
          per_page: DEFAULT_PER_PAGE,
        })

        const nextItems = Array.isArray(response?.data) ? response.data : []
        const nextForms = Array.isArray(response?.filters?.forms) ? response.filters.forms : []
        const nextMeta = response?.meta || DEFAULT_META

        setForms(nextForms)
        setMeta({
          page: nextMeta.page || page,
          per_page: nextMeta.per_page || DEFAULT_PER_PAGE,
          total_items: nextMeta.total_items || 0,
          total_pages: nextMeta.total_pages || 0,
          has_more: Boolean(nextMeta.has_more),
        })
        setPendingList(currentItems => (reset ? nextItems : mergePendingItems(currentItems, nextItems)))
      } catch (error) {
        console.log(error)
        Report.warning(
          "Oops.. something wrong",
          "Sorry, this application is experiencing problems",
          "Okay"
        )
      } finally {
        setLoading(false)
        setLoadingMore(false)
      }
    },
    [filterFormCode, filterStatus, getAccessToken]
  )

  const handleStatusFilter = status => {
    if (status === filterStatus) {
      return
    }

    setFilterStatus(status)
  }

  const handleFormFilter = formCode => {
    if (formCode === filterFormCode) {
      return
    }

    setFilterFormCode(formCode)
  }

  const getDateLabel = date => {
    const today = moment().startOf("day")
    const itemDate = moment(date).startOf("day")
    const daysDiff = today.diff(itemDate, "days")

    if (daysDiff === 0) return "Hari ini"
    if (daysDiff === 1) return "Kemarin"
    if (daysDiff === 2) return "2 Hari lalu"
    return itemDate.format("dddd, DD MMM YYYY")
  }

  const goDetail = item => {
    const formCode = item?.approval_id?.form_id?.code?.toLowerCase()

    if (!formCode) {
      return
    }

    const idDoc = item?.approval_id?.id_form_header || item?.approval_id?.form_submit_id

    navigate(`/wf/${formCode}/view/${idDoc}`, {
      state: {
        backTo: "/wf/pending",
        preferHistoryBack: true,
      },
    })
  }

  const formMenu = (
    <Menu>
      <Menu.Item key="all" onClick={() => handleFormFilter("All")}>
        <Button type="link">All</Button>
      </Menu.Item>
      {forms.map(form => (
        <Menu.Item key={form.code} onClick={() => handleFormFilter(form.code)}>
          <Button type="link">{`${form.name} (${form.count})`}</Button>
        </Menu.Item>
      ))}
    </Menu>
  )

  const statusMenu = (
    <Menu>
      {STATUS_OPTIONS.map(status => (
        <Menu.Item key={status} onClick={() => handleStatusFilter(status)}>
          <Button type="link">{status}</Button>
        </Menu.Item>
      ))}
    </Menu>
  )

  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "auto" })
    }

    setPendingList([])
    setForms([])
    setMeta(DEFAULT_META)
    void fetchPendingPage({ page: 1, reset: true })
  }, [fetchPendingPage, isAuthenticated])

  useEffect(() => {
    const target = observerTarget.current
    const observer = new IntersectionObserver(
      entries => {
        if (
          entries[0].isIntersecting &&
          meta.has_more &&
          !loading &&
          !loadingMore &&
          !loadingRef.current
        ) {
          loadingRef.current = true
          void fetchPendingPage({ page: meta.page + 1 })
        }
      },
      { threshold: 0.1 }
    )

    if (target) {
      observer.observe(target)
    }

    return () => {
      if (target) {
        observer.unobserve(target)
      }
    }
  }, [fetchPendingPage, loading, loadingMore, meta.has_more, meta.page])

  useEffect(() => {
    if (!loading && !loadingMore) {
      loadingRef.current = false
    }
  }, [loading, loadingMore])

  return (
    <Fragment>
      <Navbar title="Pending Approvals" back={true} subtitle="" navigate="/home" />
      <div className="container white-back-cr" style={{ minHeight: 500 }}>
        <div className="container mt-3 p-3">
          <div className="row">
            <div className="col-md-10 offset-md-1">
              <div className="d-flex gap-3 mb-3">
                <div>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>Status</div>
                  <Dropdown overlay={statusMenu} placement="bottomLeft" trigger={["click"]}>
                    <Button type="default" icon={<FilterOutlined />} style={{ fontSize: 12 }}>
                      {filterStatus}
                    </Button>
                  </Dropdown>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>Form</div>
                  <Dropdown overlay={formMenu} placement="bottomLeft" trigger={["click"]}>
                    <Button type="default" style={{ fontSize: 12 }}>
                      {activeForm?.name || filterFormCode}
                    </Button>
                  </Dropdown>
                </div>
              </div>

              {loading ? (
                <div className="fs-6 text-center mt-5" style={{ color: "#B7B7B7" }}>
                  <Spin size="large" />
                </div>
              ) : pendingList.length > 0 ? (
                (() => {
                  let lastDateLabel = null

                  return pendingList.map(item => {
                    const currentDateLabel = getDateLabel(item.created_at)
                    const showDateHeader = currentDateLabel !== lastDateLabel

                    if (showDateHeader) {
                      lastDateLabel = currentDateLabel
                    }

                    return (
                      <Fragment key={item.id || item._id}>
                        {showDateHeader && (
                          <p
                            className="mb-3 mt-2 text-center"
                            style={{ color: "#666", fontWeight: 500, fontSize: 14 }}
                          >
                            {currentDateLabel}
                          </p>
                        )}
                        <Badge.Ribbon
                          color={getRibbonColor(item?.approval_id?.status || filterStatus)}
                          text={item?.approval_id?.status || filterStatus}
                        >
                          <div
                            className="list-style p-3 px-4 mb-3"
                            onClick={() => goDetail(item)}
                            style={{ position: "relative" }}
                          >
                            <p
                              className="fs-7"
                              style={{
                                position: "absolute",
                                top: 12,
                                right: 16,
                                color: "#B7B7B7",
                                fontSize: 11,
                                margin: 0,
                              }}
                            >
                              {moment(item.created_at).format("dddd, DD, MMM YYYY")}
                            </p>
                            <div className="title mb-4">
                              <p className="fs-7 mb-0" style={{ paddingRight: 120 }}>
                                {item?.approval_id?.form_id?.name || "-"}
                              </p>
                            </div>
                            <div className="hour m-1">
                              <p className="fs-7" style={{ color: "#B7B7B7", fontSize: 11 }}>
                                {item?.approval_id?.uid?.fullname ?? item?.requestor?.fullname ?? "-"}
                              </p>
                            </div>
                          </div>
                        </Badge.Ribbon>
                      </Fragment>
                    )
                  })
                })()
              ) : (
                <Empty className="mt-5" />
              )}

              <div ref={observerTarget} style={{ height: 20, marginTop: 16 }} />

              {loadingMore && (
                <div className="fs-6 text-center mt-3" style={{ color: "#B7B7B7" }}>
                  <Spin />
                </div>
              )}

              {!loading && pendingList.length > 0 && !meta.has_more && (
                <p className="fs-6 text-center mt-4" style={{ color: "#B7B7B7" }}>
                  No more data
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </Fragment>
  )
}

export default WFPendingList
